import moment from 'moment/moment';

export const PASS_ANNUAL_PASS_VOUCHER = 'annualPassVoucher';
export const PASS_ANNUAL_TWENTY_FIVE_PASS_VOUCHER = 'annualTwentyFivePassVoucher';
export const PASS_FLEX_PASS = 'flexPass';
export const PASS_CONTINGENT_PASS_VOUCHER = 'contingentVoucher';

export const isFlexPass = (pass) => !!pass?.flexPass;

/**
 * Whether the "max N simultaneously open bookings" limit
 * (config.annualPass.simultaneouslyBookingLimit) applies to this pass.
 * Only AnnualPass / Family passes are limited; 25Pass and FlexPass are exempt
 * (mirrors the server check in server/api-util/annualPass.js, which only
 * counts open bookings for PASS_ANNUAL_PASS_VOUCHER). Both the BookingPanel
 * warning and the ListingPage submit guard MUST use this so they never diverge.
 *
 * @param {import('../../types/vansite').VanSite.Pass | null | undefined} pass
 * @returns {boolean}
 */
export const hasOpenBookingLimit = (pass) => !(pass?.twentyFivePass || isFlexPass(pass));

/**
 * Build an annualPassVoucher payload from a user's OWNED profile pass, adding the
 * `valid: true` + `type` the server line-items discount logic requires.
 *
 * Subscription-based passes (bought via the checkout upsell / Stripe) carry no
 * Voucherify `code`, so the server cannot validate them in the speculate — the
 * frontend asserts validity (gated on `validUntil` here, mirroring
 * hasCurrentUserValidAnnualPass). The raw profile pass already carries
 * `dateOfPublication`/`validUntil`/`included` (the coverage window
 * calculateCoveredNights needs), so spreading it yields the discount line item.
 *
 * @param {import('../../types/vansite').VanSite.Pass} annualPass
 * @returns {object|null} the voucher payload, or null when there is no valid pass
 */
export const buildOwnedAnnualPassVoucher = (annualPass) => {
  if (!annualPass) return null;
  if (!moment().utc().isBefore(moment.utc(annualPass.validUntil))) return null;
  const type = annualPass.twentyFivePass
    ? PASS_ANNUAL_TWENTY_FIVE_PASS_VOUCHER
    : isFlexPass(annualPass)
      ? PASS_FLEX_PASS
      : PASS_ANNUAL_PASS_VOUCHER;
  return {
    ...annualPass,
    type,
    valid: true,
  };
};

export const hasCurrentUserValidAnnualPass = (currentUser) => {
  if (!currentUser) return false;
  const annualPass = currentUser.attributes.profile.privateData.annualPass;
  return annualPass && moment().utc().isBefore(moment.utc(annualPass.validUntil));
};

/**
 * Mirrors the backend `thisTransactionIsTheSameDateAsAnOpenTransaction` guard
 * in `server/api-util/annualPass.js`. The backend silently strips the annual
 * pass (charging full commission) when a new booking's check-in date falls
 * inside an already-open booking's `[start, end)` range — the pass may not be
 * used on two simultaneous bookings covering the same night. We surface the
 * same condition in the UI so the user is hard-blocked before booking instead
 * of silently losing the discount.
 *
 * Keep the overlap definition identical to the backend (check-in `isSameOrAfter`
 * start AND `isBefore` end) so the two sides stay in lock-step.
 *
 * @param {import('moment').MomentInput} startDate - Selected check-in date
 * @param {Array<{attributes?: {start?: string, end?: string}}>} openBookings - Current user's open bookings
 * @returns {boolean}
 */
export const selectedDateOverlapsOpenBooking = (startDate, openBookings = []) => {
  if (!startDate) return false;
  const slotStart = moment(startDate);
  return (openBookings || []).some((booking) => {
    const start = booking?.attributes?.start;
    const end = booking?.attributes?.end;
    if (!start || !end) return false;
    return slotStart.isSameOrAfter(moment(start)) && slotStart.isBefore(moment(end));
  });
};

/**
 * True when the user holds a valid AnnualPass/Family pass AND the booking exceeds
 * the per-booking night limit that applies to pass holders. 25Pass holders use the
 * listing's own limit (enforced by the date picker), so they are exempt here.
 *
 * The limit is normally enforced by the listing date picker, but the checkout
 * upsell lets a user buy a pass AFTER picking a longer stay — this guards that
 * bypass on the checkout.
 *
 * @param {object} currentUser
 * @param {{ bookingStart: Date|string, bookingEnd: Date|string }} bookingDates
 * @param {number} passBookingLimit - config.annualPass.bookingLimit (max nights)
 * @returns {boolean}
 */
export const isAnnualPassBookingLimitExceeded = (currentUser, bookingDates, passBookingLimit) => {
  if (!hasCurrentUserValidAnnualPass(currentUser)) return false;
  const annualPass = currentUser.attributes.profile.privateData.annualPass;
  if (annualPass?.twentyFivePass) return false;
  if (!Number.isFinite(passBookingLimit)) return false;
  if (!bookingDates?.bookingStart || !bookingDates?.bookingEnd) return false;
  const nights = moment
    .utc(bookingDates.bookingEnd)
    .startOf('day')
    .diff(moment.utc(bookingDates.bookingStart).startOf('day'), 'days');
  return nights > passBookingLimit;
};

/**
 * @param {import('@vansite/ts-sharetribe-flex-sdk').CurrentUser} currentUser - The current User
 * @param {string} type - The annual Pass type
 */
export const hasCurrentUserValidAnnualPassType = (currentUser, type) => {
  if (!currentUser) return false;
  /** @type {import('../../types/vansite').VanSite.Pass} */
  const annualPass = currentUser.attributes.profile.privateData.annualPass;
  if (!annualPass) return false;
  const isValid = annualPass && moment().utc().isBefore(moment.utc(annualPass.validUntil));
  const isSameType =
    type === PASS_ANNUAL_TWENTY_FIVE_PASS_VOUCHER
      ? annualPass.twentyFivePass
      : type === PASS_FLEX_PASS
        ? isFlexPass(annualPass)
        : type === PASS_ANNUAL_PASS_VOUCHER
          ? annualPass.included?.plus === true ||
            annualPass.included?.plus === false ||
            !annualPass.included ||
            annualPass.included.plus === undefined
          : false;

  return isValid && isSameType;
};
